import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

export const CV_ADMIN_REVIEW_TOUR_SEEN_KEY = "hr_cv_admin_review_tour_seen_v1";

const TOOLTIP_HEIGHT = 300;
const TOUR_STEP_DELAY_MS = 180;
const TARGET_RETRY_COUNT = 8;
const TARGET_RETRY_DELAY_MS = 120;
const STYLE_TAG_ID = "cv-admin-review-tour-styles";

const STEPS = [
  {
    id: "toolbar",
    target: '[data-tour="cv-review-toolbar"]',
    title: "CV Review Dashboard",
    description: "This is the HR admin workspace for employee CV reviews.",
    details: [
      "Use the search box to find employees quickly.",
      "Filter by status to focus on draft, submitted, approved, or rejected CVs.",
    ],
  },
  {
    id: "mail-actions",
    target: '[data-tour="cv-review-email-actions"]',
    title: "Request And Reminder Actions",
    description: "Send fill requests, reminders, and reopen windows directly from here.",
    details: [
      "Use Remind Pending for employees who still did not submit.",
      "Use Send CV Request for one employee, multiple employees, or all.",
    ],
  },
  {
    id: "grid",
    target: '[data-tour="cv-review-grid"]',
    title: "Submissions Grid",
    description: "This table is your main queue for CV review and approval.",
    details: [
      "Open any row to review details, edit, approve, reject, or reopen.",
      "Track submission and editable-until dates before final decisions.",
    ],
  },
  {
    id: "help",
    target: '[data-tour="cv-review-help"]',
    title: "Replay Guide Anytime",
    description: "Use this ? button whenever you need to review this guide again.",
    details: [
      "The guide can be replayed at any time.",
      "Use it for onboarding new HR admin or as a quick refresher.",
    ],
  },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function getTooltipPosition(targetEl) {
  const rect = targetEl.getBoundingClientRect();
  const tooltipWidth = window.innerWidth < 640 ? window.innerWidth - 32 : 430;
  const left = clamp(rect.left, 16, window.innerWidth - tooltipWidth - 16);
  const defaultTop = rect.bottom + 14;
  const showAbove = defaultTop + TOOLTIP_HEIGHT > window.innerHeight - 16;
  const top = showAbove
    ? Math.max(16, rect.top - TOOLTIP_HEIGHT - 14)
    : defaultTop;
  const placement = showAbove ? "top" : "bottom";
  const targetCenter = rect.left + rect.width / 2;
  const arrowLeft = clamp(targetCenter - left - 8, 14, tooltipWidth - 22);

  return { top, left, width: tooltipWidth, placement, arrowLeft };
}

export default function CvAdminReviewTour({
  isReady,
  forceStart,
  restartSignal = 0,
  onFinish,
}) {
  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetEl, setTargetEl] = useState(null);
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (document.getElementById(STYLE_TAG_ID)) return undefined;

    const style = document.createElement("style");
    style.id = STYLE_TAG_ID;
    style.textContent = `
      @keyframes cvAdminTourPulse {
        0% { box-shadow: 0 0 0 0 rgba(66, 165, 245, 0.45); }
        70% { box-shadow: 0 0 0 14px rgba(66, 165, 245, 0); }
        100% { box-shadow: 0 0 0 0 rgba(66, 165, 245, 0); }
      }
      @keyframes cvAdminTourPopIn {
        0% { transform: translateY(8px) scale(0.98); opacity: 0; }
        100% { transform: translateY(0) scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  const finishTour = useCallback(
    (markSeen = true) => {
      clearTimers();
      if (markSeen) localStorage.setItem(CV_ADMIN_REVIEW_TOUR_SEEN_KEY, "1");
      setRunning(false);
      setTargetEl(null);
      onFinish?.();
    },
    [clearTimers, onFinish]
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!isReady) return;
    const seen = localStorage.getItem(CV_ADMIN_REVIEW_TOUR_SEEN_KEY) === "1";
    if (forceStart || restartSignal > 0 || !seen) {
      setRunning(true);
      setStepIndex(0);
    }
  }, [forceStart, restartSignal, isReady]);

  const currentStep = STEPS[stepIndex];
  const resolvedCurrentStep = useMemo(() => currentStep || null, [currentStep]);

  useEffect(() => {
    if (!running || !currentStep) return;

    clearTimers();

    const resolveTarget = (attempt = 0) => {
      const el = document.querySelector(currentStep.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        setTargetEl(el);
        return;
      }

      if (attempt < TARGET_RETRY_COUNT) {
        const retryId = window.setTimeout(
          () => resolveTarget(attempt + 1),
          TARGET_RETRY_DELAY_MS
        );
        timersRef.current.push(retryId);
        return;
      }

      if (stepIndex < STEPS.length - 1) {
        setStepIndex((prev) => prev + 1);
      } else {
        finishTour(true);
      }
    };

    const startId = window.setTimeout(() => resolveTarget(0), TOUR_STEP_DELAY_MS);
    timersRef.current.push(startId);
  }, [clearTimers, currentStep, finishTour, running, stepIndex]);

  useEffect(() => {
    if (!running || !targetEl) return;

    const prev = {
      outline: targetEl.style.outline,
      outlineOffset: targetEl.style.outlineOffset,
      position: targetEl.style.position,
      zIndex: targetEl.style.zIndex,
      animation: targetEl.style.animation,
      boxShadow: targetEl.style.boxShadow,
      borderRadius: targetEl.style.borderRadius,
    };

    targetEl.style.outline = "3px solid rgba(144, 202, 249, 0.98)";
    targetEl.style.outlineOffset = "3px";
    targetEl.style.zIndex = "1301";
    targetEl.style.animation = "cvAdminTourPulse 1.6s ease-out infinite";
    targetEl.style.boxShadow = "0 0 0 8px rgba(33, 150, 243, 0.16)";
    if (!targetEl.style.borderRadius) targetEl.style.borderRadius = "8px";
    if (!prev.position || prev.position === "static") {
      targetEl.style.position = "relative";
    }

    return () => {
      targetEl.style.outline = prev.outline;
      targetEl.style.outlineOffset = prev.outlineOffset;
      targetEl.style.position = prev.position;
      targetEl.style.zIndex = prev.zIndex;
      targetEl.style.animation = prev.animation;
      targetEl.style.boxShadow = prev.boxShadow;
      targetEl.style.borderRadius = prev.borderRadius;
    };
  }, [running, targetEl]);

  if (!running || !resolvedCurrentStep || !targetEl) return null;

  const pos = getTooltipPosition(targetEl);
  const progress = ((stepIndex + 1) / Math.max(STEPS.length, 1)) * 100;

  return (
    <>
      <Backdrop
        open
        sx={{
          zIndex: 1300,
          bgcolor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(1px)",
        }}
      />

      <Paper
        key={resolvedCurrentStep.id}
        elevation={8}
        sx={{
          position: "fixed",
          zIndex: 1302,
          top: pos.top,
          left: pos.left,
          width: pos.width,
          borderRadius: 2,
          p: 2,
          animation: "cvAdminTourPopIn 220ms ease-out",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 12,
            height: 12,
            bgcolor: "background.paper",
            transform: "rotate(45deg)",
            left: pos.arrowLeft,
            ...(pos.placement === "bottom" ? { top: -6 } : { bottom: -6 }),
          }}
        />

        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" sx={{ fontWeight: 900, color: "text.secondary" }}>
              CV Admin Product Tour
            </Typography>
            <Chip
              size="small"
              label={`Step ${stepIndex + 1}/${STEPS.length}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {resolvedCurrentStep.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {resolvedCurrentStep.description}
          </Typography>

          {!!resolvedCurrentStep.details?.length && (
            <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
              {resolvedCurrentStep.details.map((detail, idx) => (
                <Box key={`${resolvedCurrentStep.id}-d-${idx}`} component="li" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ pt: 0.25 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 99 }}
            />
          </Box>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
              disabled={stepIndex === 0}
            >
              Back
            </Button>

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" onClick={() => finishTour(true)}>
                Skip
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (stepIndex >= STEPS.length - 1) {
                    finishTour(true);
                    return;
                  }
                  setStepIndex((prev) => prev + 1);
                }}
              >
                {stepIndex >= STEPS.length - 1 ? "Finish" : "Next"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}
